// ============================================
// Auction Search Page - Main auction browser
// ============================================

import { useState, useEffect, useCallback, useRef } from "react";
import {
  AppConfig,
  DomainItem,
  AuctionFilters,
  DEFAULT_AUCTION_FILTERS,
  AIAnalysisResult,
} from "../../types";
import { DropCatchService } from "../../services/dropcatchService";
import { GeminiService } from "../../services/geminiService";
import { FilterPanel, FilterBar } from "../shared/FilterPanel";
import { Pagination, usePagination } from "../shared/Pagination";
import { InlineCountdown } from "../shared/CountdownTimer";

// Bid Modal Component
function BidModal({
  domain,
  onClose,
  onBid
}: {
  domain: DomainItem;
  onClose: () => void;
  onBid: (amount: number) => Promise<void>;
}) {
  const [bidAmount, setBidAmount] = useState<number>(domain.minimumNextBid || domain.price + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (bidAmount < (domain.minimumNextBid || domain.price + 1)) {
      setError(`Minimum bid is $${domain.minimumNextBid || domain.price + 1}`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onBid(bidAmount);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bid failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-black border-2 border-green-500 w-full max-w-md" style={{ boxShadow: '0 0 30px rgba(34, 197, 94, 0.3)' }}>
        <div className="px-4 py-3 border-b-2 border-green-500 flex items-center justify-between">
          <h2 className="text-lg font-bold text-green-400">PLACE BID</h2>
          <button onClick={onClose} className="text-green-500 hover:text-green-300 text-xl">×</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="p-3 border border-green-700 bg-green-900/10">
            <div className="text-green-400 font-mono text-lg">{domain.name}</div>
            <div className="flex justify-between mt-2 text-sm">
              <span className="text-green-600">Current Bid:</span>
              <span className="text-green-400 font-bold">${domain.price.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-green-600">Minimum Next:</span>
              <span className="text-green-400">${(domain.minimumNextBid || domain.price + 1).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-green-600">Ends:</span>
              <InlineCountdown endTime={domain.endTime} />
            </div>
          </div>

          <div>
            <label className="block text-xs text-green-600 mb-2">YOUR MAX BID ($)</label>
            <input
              type="number"
              value={bidAmount}
              onChange={(e) => setBidAmount(Number(e.target.value))}
              min={domain.minimumNextBid || domain.price + 1}
              className="w-full px-4 py-3 bg-black border-2 border-green-500 text-green-400 text-xl font-mono focus:outline-none"
            />
            <p className="text-xs text-green-700 mt-1">
              This is your maximum proxy bid. You may win for less.
            </p>
          </div>

          {error && (
            <div className="p-2 border border-red-500 bg-red-900/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2 border border-green-700 text-green-500 hover:border-green-500"
            >
              CANCEL
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-2 bg-green-500 text-black font-bold hover:bg-green-400 disabled:opacity-50"
            >
              {loading ? 'PLACING...' : `BID $${bidAmount.toLocaleString()}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AuctionSearchProps {
  config: AppConfig;
  dropcatchService: DropCatchService;
  geminiService: GeminiService;
}

/**
 * AuctionSearch Component
 *
 * Main auction search page with:
 * - Full filter panel matching dropcatch.com
 * - Pagination with cursor support
 * - Live countdown timers
 * - AI analysis integration
 * - Bulk selection for batch operations
 */
export function AuctionSearch({
  config,
  dropcatchService,
  geminiService,
}: AuctionSearchProps) {
  // State
  const [filters, setFilters] = useState<AuctionFilters>({
    ...DEFAULT_AUCTION_FILTERS,
    tlds: config.tlds || ["com"],
    auctionTypes: config.auction_types || ["Dropped", "PrivateSeller"],
    noDashes: config.no_dashes,
    noNumbers: config.no_numbers,
  });
  const [results, setResults] = useState<DomainItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(true);
  const [analyzing, setAnalyzing] = useState<string | null>(null);
  const [bidModal, setBidModal] = useState<DomainItem | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [sortingByAi, setSortingByAi] = useState(false);

  // Pagination state
  const pagination = usePagination(50);

  // Load initial results on mount
  useEffect(() => {
    handleSearch();
  }, []);

  // Search handler
  const handleSearch = useCallback(
    async (cursor?: { previous?: string; next?: string }) => {
      setLoading(true);
      setError(null);

      try {
        const response = await dropcatchService.searchAuctions({
          searchTerm: filters.searchTerm || undefined,
          size: pagination.pageSize,
          previous: cursor?.previous,
          next: cursor?.next,
          showAllActive: true,
          hasBids: filters.hasBids,
          tlds: filters.tlds,
          types: filters.auctionTypes,
          highBidMin: filters.minPrice ?? undefined,
          highBidMax: filters.maxPrice ?? undefined,
          sort: filters.sort,
        });

        setResults(response.items);
        pagination.updateCursors(
          response.previous || null,
          response.next || null,
          response.items.length > 0 ? 1000 : 0 // API doesn't return total, estimate
        );
        pagination.setTotalItems(response.items.length > 0 ? 1000 : 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Search failed");
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [dropcatchService, filters, pagination]
  );

  // Pagination handlers
  const handleNextPage = useCallback(() => {
    if (pagination.cursors.next) {
      pagination.goToNext();
      handleSearch({ next: pagination.cursors.next });
    }
  }, [pagination, handleSearch]);

  const handlePrevPage = useCallback(() => {
    if (pagination.cursors.previous) {
      pagination.goToPrevious();
      handleSearch({ previous: pagination.cursors.previous });
    }
  }, [pagination, handleSearch]);

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      pagination.changePageSize(newSize);
      handleSearch();
    },
    [pagination, handleSearch]
  );

  // Filter change handler - auto-search on filter change
  const handleFiltersChange = useCallback((newFilters: Partial<AuctionFilters>) => {
    setFilters((prev) => {
      const updated = { ...prev, ...newFilters };
      // Auto-search after filter change (debounced)
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch();
      }, 300);
      return updated;
    });
    pagination.reset();
  }, [pagination, handleSearch]);

  // Handle search input - separate from filters
  const handleSearchInput = useCallback((value: string) => {
    setSearchInput(value);
    setFilters(prev => ({ ...prev, searchTerm: value }));
  }, []);

  // Handle search on Enter key
  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // Handle placing a bid
  const handlePlaceBid = useCallback(async (domain: DomainItem, amount: number) => {
    const auctionId = parseInt(domain.id);
    const result = await dropcatchService.placeBids([{ auctionId, amount }]);
    if (result.failures && result.failures.length > 0) {
      throw new Error(result.failures[0].error?.description || 'Bid failed');
    }
    // Refresh results after successful bid
    handleSearch();
  }, [dropcatchService, handleSearch]);

  // Selection handlers
  const toggleSelection = useCallback((domainId: string) => {
    setSelectedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domainId)) {
        next.delete(domainId);
      } else {
        next.add(domainId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedDomains(new Set(results.map((d) => d.id)));
  }, [results]);

  const clearSelection = useCallback(() => {
    setSelectedDomains(new Set());
  }, []);

  // AI Analysis handler
  const handleAnalyze = useCallback(
    async (domain: DomainItem) => {
      setAnalyzing(domain.id);
      try {
        const analysis = await geminiService.analyzeDomain(domain.name, domain.price);
        // Update the domain in results with analysis
        setResults((prev) =>
          prev.map((d) =>
            d.id === domain.id ? { ...d, aiAnalysis: analysis } : d
          )
        );
      } catch (err) {
        console.error("Analysis failed:", err);
      } finally {
        setAnalyzing(null);
      }
    },
    [geminiService]
  );

  // Quick check handler
  const handleQuickCheck = useCallback(
    async (domain: DomainItem) => {
      setAnalyzing(domain.id);
      try {
        const verdict = await geminiService.quickCheck(domain.name);
        // Create a partial analysis result
        const partialAnalysis: AIAnalysisResult = {
          valuation: "Quick check only",
          verdict: verdict.toLowerCase().includes("yes")
            ? "Top Pick"
            : verdict.toLowerCase().includes("maybe")
            ? "Backorder"
            : "Pass",
          analysis: verdict,
          seo_notes: "",
        };
        setResults((prev) =>
          prev.map((d) =>
            d.id === domain.id ? { ...d, aiAnalysis: partialAnalysis } : d
          )
        );
      } catch (err) {
        console.error("Quick check failed:", err);
      } finally {
        setAnalyzing(null);
      }
    },
    [geminiService]
  );

  // Batch analyze selected domains
  const handleBatchAnalyze = useCallback(async () => {
    if (selectedDomains.size === 0) return;

    const domainsToAnalyze = results
      .filter((d) => selectedDomains.has(d.id))
      .slice(0, 50); // Max 50 at a time

    setLoading(true);
    try {
      const domainNames = domainsToAnalyze.map((d) => d.name);
      const batchResults = await geminiService.batchQuickCheck(domainNames);

      // Update results with batch analysis
      setResults((prev) =>
        prev.map((d) => {
          const result = batchResults.get(d.name);
          if (result) {
            const partialAnalysis: AIAnalysisResult = {
              valuation: "Batch check",
              verdict: result.viable ? "Top Pick" : "Pass",
              analysis: result.reason,
              seo_notes: "",
            };
            return { ...d, aiAnalysis: partialAnalysis };
          }
          return d;
        })
      );
    } catch (err) {
      setError("Batch analysis failed");
    } finally {
      setLoading(false);
    }
  }, [selectedDomains, results, geminiService]);

  // Sort by AI Valuation - analyzes all domains and sorts by estimated value
  const handleSortByAiValuation = useCallback(async () => {
    if (results.length === 0) return;

    setSortingByAi(true);
    setError(null);

    try {
      // Analyze domains that don't have analysis yet (max 50 at a time)
      const domainsToAnalyze = results.filter(d => !d.aiAnalysis).slice(0, 50);

      if (domainsToAnalyze.length > 0) {
        const domainNames = domainsToAnalyze.map(d => d.name);
        const batchResults = await geminiService.batchQuickCheck(domainNames);

        // Update results with batch analysis
        setResults(prev => prev.map(d => {
          const result = batchResults.get(d.name);
          if (result && !d.aiAnalysis) {
            const partialAnalysis: AIAnalysisResult = {
              valuation: result.viable ? "$500-$5000" : "$10-$100",
              verdict: result.viable ? "Top Pick" : "Pass",
              analysis: result.reason,
              seo_notes: "",
            };
            return { ...d, aiAnalysis: partialAnalysis };
          }
          return d;
        }));
      }

      // Now sort by valuation (Top Picks first, then Consider, then Pass)
      setResults(prev => {
        const sorted = [...prev].sort((a, b) => {
          const getScore = (domain: DomainItem) => {
            if (!domain.aiAnalysis) return 0;
            const verdict = domain.aiAnalysis.verdict?.toLowerCase() || '';
            if (verdict.includes('top') || verdict.includes('pick')) return 3;
            if (verdict.includes('consider') || verdict.includes('back') || verdict.includes('maybe')) return 2;
            return 1;
          };
          return getScore(b) - getScore(a);
        });
        return sorted;
      });

    } catch (err) {
      setError("AI valuation sort failed");
    } finally {
      setSortingByAi(false);
    }
  }, [results, geminiService]);

  // Get verdict badge styling
  const getVerdictBadge = (verdict?: string) => {
    if (!verdict) return null;
    const lower = verdict.toLowerCase();
    if (lower.includes("top") || lower.includes("pick")) {
      return (
        <span className="px-2 py-0.5 bg-green-500 text-black text-xs font-bold">
          TOP PICK
        </span>
      );
    }
    if (lower.includes("back") || lower.includes("maybe")) {
      return (
        <span className="px-2 py-0.5 bg-yellow-500 text-black text-xs font-bold">
          CONSIDER
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 bg-red-500 text-black text-xs font-bold">
        PASS
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b-2 border-green-500">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl text-green-400 tracking-wider font-bold">
            AUCTION SEARCH
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-1 border border-green-700 text-green-500 text-sm tracking-wider hover:border-green-500"
            >
              {showFilters ? "HIDE FILTERS" : "SHOW FILTERS"}
            </button>
            <button
              onClick={() => handleSearch()}
              disabled={loading}
              className="px-4 py-1 bg-green-500 text-black font-bold text-sm tracking-wider hover:bg-green-400 disabled:opacity-50"
            >
              {loading ? "LOADING..." : "REFRESH"}
            </button>
          </div>
        </div>

        {/* Search bar - prominent and separate from filters */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search domains (e.g., crypto, ai, tech)..."
              className="w-full px-4 py-2 bg-black border-2 border-green-500 text-green-400 font-mono placeholder-green-700 focus:outline-none focus:border-green-400"
              style={{ boxShadow: '0 0 10px rgba(34, 197, 94, 0.2)' }}
            />
            {searchInput && (
              <button
                onClick={() => {
                  setSearchInput('');
                  setFilters(prev => ({ ...prev, searchTerm: '' }));
                  handleSearch();
                }}
                className="absolute right-12 top-1/2 -translate-y-1/2 text-green-600 hover:text-green-400 px-2"
              >
                ×
              </button>
            )}
          </div>
          <button
            onClick={() => handleSearch()}
            disabled={loading}
            className="px-6 py-2 bg-green-500 text-black font-bold tracking-wider hover:bg-green-400 disabled:opacity-50"
          >
            SEARCH
          </button>
        </div>

        {/* Active filters bar */}
        <FilterBar filters={filters} onFiltersChange={handleFiltersChange} />
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Filter panel (collapsible sidebar) */}
        {showFilters && (
          <div className="w-72 border-r border-green-700 overflow-y-auto">
            <FilterPanel
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onSearch={() => handleSearch()}
              loading={loading}
            />
          </div>
        )}

        {/* Results area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Error display */}
          {error && (
            <div className="m-4 p-3 border-2 border-red-500 bg-red-900/20 text-red-400 text-sm">
              ERROR: {error}
              <button
                onClick={() => setError(null)}
                className="ml-4 text-red-500 hover:text-red-300"
              >
                [DISMISS]
              </button>
            </div>
          )}

          {/* Results header */}
          <div className="p-4 border-b border-green-700 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-green-400">
                {results.length} DOMAINS FOUND
              </span>
              {selectedDomains.size > 0 && (
                <span className="text-sm text-green-600">
                  ({selectedDomains.size} SELECTED)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSortByAiValuation}
                disabled={sortingByAi || results.length === 0}
                className="px-3 py-1 bg-purple-600 text-white text-xs font-bold tracking-wider hover:bg-purple-500 disabled:opacity-50"
                title="Analyze all domains and sort by AI valuation (Top Picks first)"
              >
                {sortingByAi ? 'SORTING...' : 'SORT BY AI'}
              </button>
              {selectedDomains.size > 0 && (
                <>
                  <button
                    onClick={handleBatchAnalyze}
                    disabled={loading}
                    className="px-3 py-1 border border-green-500 text-green-500 text-xs tracking-wider hover:bg-green-500 hover:text-black disabled:opacity-50"
                  >
                    ANALYZE ({selectedDomains.size})
                  </button>
                  <button
                    onClick={clearSelection}
                    className="px-3 py-1 border border-green-700 text-green-600 text-xs tracking-wider hover:border-green-500"
                  >
                    CLEAR
                  </button>
                </>
              )}
              <button
                onClick={selectAll}
                className="px-3 py-1 border border-green-700 text-green-600 text-xs tracking-wider hover:border-green-500"
              >
                SELECT ALL
              </button>
            </div>
          </div>

          {/* Results table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-black border-b-2 border-green-500">
                <tr className="text-xs text-green-600 tracking-wider">
                  <th className="p-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={
                        selectedDomains.size > 0 &&
                        selectedDomains.size === results.length
                      }
                      onChange={(e) =>
                        e.target.checked ? selectAll() : clearSelection()
                      }
                      className="accent-green-500"
                    />
                  </th>
                  <th className="p-3 text-left">DOMAIN</th>
                  <th className="p-3 text-left w-32">TIME LEFT</th>
                  <th className="p-3 text-left w-24">TYPE</th>
                  <th className="p-3 text-right w-24">HIGH BID</th>
                  <th className="p-3 text-center w-20">BIDS</th>
                  <th className="p-3 text-center w-28">AI VERDICT</th>
                  <th className="p-3 text-center w-40">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading && results.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center">
                      <span className="text-green-500 animate-pulse text-sm tracking-wider">
                        SEARCHING...
                      </span>
                    </td>
                  </tr>
                ) : results.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center">
                      <span className="text-green-700 text-sm tracking-wider">
                        NO RESULTS FOUND. TRY ADJUSTING FILTERS.
                      </span>
                    </td>
                  </tr>
                ) : (
                  results.map((domain) => (
                    <tr
                      key={domain.id}
                      className={`border-b border-green-900 hover:bg-green-900/20 transition-colors ${
                        selectedDomains.has(domain.id) ? "bg-green-900/30" : ""
                      }`}
                    >
                      {/* Checkbox */}
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedDomains.has(domain.id)}
                          onChange={() => toggleSelection(domain.id)}
                          className="accent-green-500"
                        />
                      </td>

                      {/* Domain name */}
                      <td className="p-3">
                        <a
                          href={`https://www.dropcatch.com/auction/${domain.name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-400 hover:text-green-300 font-mono"
                        >
                          {domain.name}
                        </a>
                        {domain.winning && (
                          <span className="ml-2 px-1 py-0.5 bg-green-500 text-black text-xs">
                            WINNING
                          </span>
                        )}
                      </td>

                      {/* Time remaining */}
                      <td className="p-3">
                        <InlineCountdown endTime={domain.endTime} />
                      </td>

                      {/* Auction type */}
                      <td className="p-3">
                        <span className="text-xs text-green-600 tracking-wider">
                          {domain.status === "Dropped"
                            ? "DROP"
                            : domain.status === "PrivateSeller"
                            ? "SELLER"
                            : "PRE-REL"}
                        </span>
                      </td>

                      {/* High bid */}
                      <td className="p-3 text-right">
                        <span className="text-green-400 font-mono">
                          ${domain.price.toLocaleString()}
                        </span>
                      </td>

                      {/* Number of bidders */}
                      <td className="p-3 text-center">
                        <span className="text-green-600">{domain.bidders}</span>
                      </td>

                      {/* AI verdict */}
                      <td className="p-3 text-center">
                        {analyzing === domain.id ? (
                          <span className="text-green-500 animate-pulse text-xs">
                            ANALYZING...
                          </span>
                        ) : domain.aiAnalysis ? (
                          getVerdictBadge(domain.aiAnalysis.verdict)
                        ) : (
                          <span className="text-green-800 text-xs">-</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleQuickCheck(domain)}
                            disabled={analyzing === domain.id}
                            className="px-2 py-1 bg-blue-600 text-white text-xs font-bold hover:bg-blue-500 disabled:opacity-50"
                            title="Quick AI viability check - fast yes/no/maybe"
                          >
                            QUICK
                          </button>
                          <button
                            onClick={() => handleAnalyze(domain)}
                            disabled={analyzing === domain.id}
                            className="px-2 py-1 bg-purple-600 text-white text-xs font-bold hover:bg-purple-500 disabled:opacity-50"
                            title="Deep AI analysis with valuation and SEO notes"
                          >
                            ANALYZE
                          </button>
                          <button
                            onClick={() => setBidModal(domain)}
                            className="px-2 py-1 bg-green-500 text-black text-xs font-bold hover:bg-green-400"
                          >
                            BID
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-green-700">
            <Pagination
              currentPage={pagination.currentPage}
              totalItems={pagination.totalItems}
              pageSize={pagination.pageSize}
              hasNext={pagination.hasNext}
              hasPrevious={pagination.hasPrevious}
              onNext={handleNextPage}
              onPrevious={handlePrevPage}
              onPageSizeChange={handlePageSizeChange}
              loading={loading}
            />
          </div>
        </div>
      </div>

      {/* Bid Modal */}
      {bidModal && (
        <BidModal
          domain={bidModal}
          onClose={() => setBidModal(null)}
          onBid={(amount) => handlePlaceBid(bidModal, amount)}
        />
      )}
    </div>
  );
}

export default AuctionSearch;
